import {
  ConsentBanner,
  ConsentDialog,
  ConsentManagerProvider,
  policyPackPresets,
  useConsentManager,
} from "@c15t/react";
import { type ReactNode, useEffect } from "react";
import type { VisitorGeo } from "~/lib/geo";
import { buildConsentScripts } from "./scripts";
import { consentTheme } from "./theme";
import { buildC15tI18n } from "./translations";

/**
 * SSR jurisdiction default. The real visitor country is intentionally unknown at
 * render time so the SSR document is request-independent and safe to CDN-cache.
 * We default to the EU opt-in regime, which fails safe: the banner shows and
 * consent-gated scripts stay blocked until `GeoOverrideSync` resolves the
 * visitor's actual country on the client. This prevents a cached response
 * generated for one country from imposing its consent regime on another visitor.
 */
const DEFAULT_JURISDICTION_COUNTRY = "DE";

interface ConsentManagerProps {
  readonly children: ReactNode;
  readonly geo?: VisitorGeo;
}

export function ConsentManager({ children, geo }: ConsentManagerProps) {
  return (
    <ConsentManagerProvider
      options={{
        consentCategories: ["necessary", "measurement", "marketing"],
        i18n: buildC15tI18n(),
        mode: "offline",
        offlinePolicy: {
          policyPacks: [
            policyPackPresets.europeOptIn(),
            policyPackPresets.californiaOptOut(),
            policyPackPresets.quebecOptIn(),
            policyPackPresets.worldNoBanner(),
          ],
        },
        overrides: { country: DEFAULT_JURISDICTION_COUNTRY },
        scripts: buildConsentScripts(),
        theme: consentTheme,
      }}
    >
      <GeoOverrideSync geo={geo} />
      <ConsentBanner hideBranding={true} />
      <ConsentDialog hideBranding={true} />
      {children}
    </ConsentManagerProvider>
  );
}

/**
 * Applies the visitor's real, client-resolved geolocation to the consent
 * jurisdiction after hydration. It runs only once `geo` has been fetched on the
 * client, so the shared SSR response never varies by visitor country.
 */
function GeoOverrideSync({ geo }: { readonly geo?: VisitorGeo }) {
  const { setOverrides } = useConsentManager();
  const country = geo?.country;
  const region = geo?.region;
  useEffect(() => {
    if (!country) {
      return;
    }
    setOverrides({ country, region });
  }, [country, region, setOverrides]);
  return null;
}
